/**
 * LogicCraft - Célula Didática 3D da Esteira Seletora / Gêmeo Digital (main-conveyor-cell.js)
 * Módulo 100% Autônomo e Auto-Contido.
 *
 * Responsável por:
 * 1. Modelagem 3D industrial de alta fidelidade da esteira transportadora, motorredutor,
 *    sensores (óptico e indutivo), cilindro empurrador, rampa de descarte e caixas coletoras.
 * 2. Régua de bornes de E/S integrada à simulação de fios (jumpers banana) do LogicCraft.
 * 3. Física e dinâmica de transporte de peças (metal vs plástico) em tempo real.
 * 4. Integração nativa com o motor de áudio Web Audio API (LogicCraft.audio).
 * 5. Telemetria, placar pedagógico e interface de controle flutuante (HUD).
 */

'use strict';

window.LogicCraft = window.LogicCraft || {};

window.LogicCraft.conveyorCell = (function() {
    let isActive = false;
    let cellGroup = null;
    let beltMesh = null;
    let beltTex = null;
    let motorFanMesh = null;
    let pusherPistonMesh = null;
    let pusherShoeMesh = null;
    let ledOptMesh = null;
    let ledIndMesh = null;
    let laserBeamMesh = null;

    // Estado da esteira e atuadores
    let isBeltRunning = false;
    let beltSpeed = 1.35; // metros por segundo na simulação
    let pusherExtension = 0.0; // 0.0 (recuado) a 1.0 (avançado)
    let pusherTarget = 0.0;

    // Sensores de fim de curso do cilindro empurrador
    let sensorS0 = true;  // Recuado
    let sensorS1 = false; // Avançado

    // Sensores da linha de triagem
    let sensorOptActive = false; // Presença de qualquer peça
    let sensorIndActive = false; // Presença de peça metálica

    // Posições dos sensores ao longo do eixo da esteira (X local)
    // Posições dos sensores ao longo do eixo da esteira (X local ampliado para 6.8m)
    const POS_X_OPT = -1.10;  // Sensor óptico de barreira (detecta presença de qualquer peça)
    const POS_X_IND = 0.40;   // Sensor indutivo de proximidade (detecta apenas metal)
    const POS_X_PUSHER = 1.35; // Posição do cilindro empurrador pneumático
    const POS_X_END = 3.35;   // Fim da esteira (queda na caixa de plásticos)

    // Lista de peças ativas no processo e fila de alimentação anti-colisão
    let pieces = [];
    let pieceQueue = [];
    let spawnTimer = 0.0;
    const SPAWN_INTERVAL = 3.8; // segundos entre novas peças

    // Estatísticas pedagógicas da triagem
    let stats = {
        totalSpawned: 0,
        metalsCorrect: 0,
        plasticsCorrect: 0,
        sortingErrors: 0
    };

    // Coordenadas mundiais da célula no espaço 3D (ao fundo da bancada)
    const CELL_WORLD_POS = { x: 0.0, y: 0.0, z: -2.8 };

    // Console frontal de bornes de E/S na base da máquina (Y=0.22, Z=0.85 para não obstruir o tapete)
    const IO_TERMINALS = {
        CMD_ESTEIRA:   { id: 'CMD_ESTEIRA',   relX: -1.25, relZ: 0.85, isInput: true,  state: false, label: 'LIGAR ESTEIRA' },
        CMD_EMPURRADOR:{ id: 'CMD_EMPURRADOR',relX: -0.75, relZ: 0.85, isInput: true,  state: false, label: 'EMP. PISTÃO' },
        S_OPT:         { id: 'S_OPT',         relX: -0.25, relZ: 0.85, isInput: false, state: false, label: 'SENS. ÓPTICO' },
        S_IND:         { id: 'S_IND',         relX:  0.25, relZ: 0.85, isInput: false, state: false, label: 'SENS. INDUTIVO' },
        S0_REC:        { id: 'S0_REC',        relX:  0.75, relZ: 0.85, isInput: false, state: true,  label: 'S0 RECUADO' },
        S1_AV:         { id: 'S1_AV',         relX:  1.25, relZ: 0.85, isInput: false, state: false, label: 'S1 AVANÇADO' }
    };

    /**
     * Cria textura industrial com ranhuras em borracha e faixas amarelas de segurança
     */
    function createBeltTexture() {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');

        // Fundo emborrachado preto fosco
        ctx.fillStyle = '#0B1120';
        ctx.fillRect(0, 0, 512, 128);

        // Ranhuras antiderrapantes em relevo
        ctx.fillStyle = '#1E293B';
        for (let x = 0; x < 512; x += 32) {
            ctx.fillRect(x, 0, 16, 128);
        }

        // Faixas de segurança industrial amarelas e pretas nas bordas
        ctx.fillStyle = '#EAB308';
        ctx.fillRect(0, 0, 512, 8);
        ctx.fillRect(0, 120, 512, 8);

        ctx.fillStyle = '#000000';
        for (let x = 0; x < 512; x += 24) {
            ctx.fillRect(x, 0, 12, 8);
            ctx.fillRect(x, 120, 12, 8);
        }

        const tex = new THREE.CanvasTexture(canvas);
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(6, 1);
        return tex;
    }

    /**
     * Constrói o modelo 3D completo e realista da Célula de Manufatura
     */
    function buildCell3DModel() {
        const group = new THREE.Group();
        group.position.set(CELL_WORLD_POS.x, CELL_WORLD_POS.y, CELL_WORLD_POS.z);

        // Materiais industriais de alta fidelidade
        const matAlum = new THREE.MeshStandardMaterial({ color: 0x94A3B8, metalness: 0.85, roughness: 0.25 });
        const matAlumDark = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.75, roughness: 0.35 });
        const matBlue = new THREE.MeshStandardMaterial({ color: 0x1D4ED8, metalness: 0.45, roughness: 0.30 });
        const matSteel = new THREE.MeshStandardMaterial({ color: 0xF8FAFC, metalness: 0.98, roughness: 0.12 });
        const matBrass = new THREE.MeshStandardMaterial({ color: 0xD97706, metalness: 0.88, roughness: 0.22 });
        const matDark = new THREE.MeshStandardMaterial({ color: 0x0F172A, roughness: 0.85 });
        const matRubber = new THREE.MeshStandardMaterial({ color: 0x090D16, roughness: 0.92 });
        const matYellow = new THREE.MeshStandardMaterial({ color: 0xEAB308, roughness: 0.35 });

        // 1. ESTRUTURA MODULAR TIPO BOSCH-REXROTH (8 Pernas com Sapatas e Travessas)
        const legGeo = new THREE.BoxGeometry(0.08, 0.90, 0.08);
        const footGeo = new THREE.CylinderGeometry(0.065, 0.075, 0.04, 16);
        const legPositions = [
            [-3.15, 0.45, -0.44], [-3.15, 0.45, 0.44],
            [-1.05, 0.45, -0.44], [-1.05, 0.45, 0.44],
            [ 1.05, 0.45, -0.44], [ 1.05, 0.45, 0.44],
            [ 3.15, 0.45, -0.44], [ 3.15, 0.45, 0.44]
        ];

        legPositions.forEach(([x, y, z]) => {
            const leg = new THREE.Mesh(legGeo, matAlum);
            leg.position.set(x, y, z);
            const foot = new THREE.Mesh(footGeo, matRubber);
            foot.position.set(x, 0.02, z);
            group.add(leg, foot);
        });

        // Travessas estruturais longitudinais e transversais inferiores
        const tieBarGeo = new THREE.BoxGeometry(6.40, 0.05, 0.05);
        const tieF = new THREE.Mesh(tieBarGeo, matAlumDark);
        tieF.position.set(0, 0.20, 0.44);
        const tieB = new THREE.Mesh(tieBarGeo, matAlumDark);
        tieB.position.set(0, 0.20, -0.44);
        group.add(tieF, tieB);

        [-3.15, -1.05, 1.05, 3.15].forEach(x => {
            const crossBar = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.88), matAlumDark);
            crossBar.position.set(x, 0.20, 0);
            group.add(crossBar);
        });

        // Longarinas principais do chassi superior (Perfil estrutural com canaletas)
        const frameBeamGeo = new THREE.BoxGeometry(6.80, 0.12, 0.05);
        const frameF = new THREE.Mesh(frameBeamGeo, matAlumDark);
        frameF.position.set(0, 0.86, 0.43);
        const frameB = new THREE.Mesh(frameBeamGeo, matAlumDark);
        frameB.position.set(0, 0.86, -0.43);
        group.add(frameF, frameB);

        // Guias laterais cilíndricas em aço inox escovado (canalizam o fluxo de peças)
        const guideGeo = new THREE.CylinderGeometry(0.016, 0.016, 6.70, 16);
        const guideF = new THREE.Mesh(guideGeo, matSteel);
        guideF.rotation.z = Math.PI / 2;
        guideF.position.set(0, 0.96, 0.38);
        const guideB = new THREE.Mesh(guideGeo, matSteel);
        guideB.rotation.z = Math.PI / 2;
        guideB.position.set(0, 0.96, -0.38);
        group.add(guideF, guideB);

        // Suportes azuis das guias laterais
        [-2.8, -1.4, 0.0, 1.4, 2.8].forEach(x => {
            const bracketGeo = new THREE.BoxGeometry(0.03, 0.10, 0.04);
            const brF = new THREE.Mesh(bracketGeo, matBlue);
            brF.position.set(x, 0.93, 0.41);
            const brB = new THREE.Mesh(bracketGeo, matBlue);
            brB.position.set(x, 0.93, -0.41);
            group.add(brF, brB);
        });

        // 2. TAPETE DA ESTEIRA (Borracha reforçada de 6.68m)
        beltTex = createBeltTexture();
        const matBelt = new THREE.MeshStandardMaterial({ map: beltTex, roughness: 0.88, metalness: 0.1 });
        const beltGeo = new THREE.BoxGeometry(6.68, 0.025, 0.80);
        beltMesh = new THREE.Mesh(beltGeo, matBelt);
        beltMesh.position.set(0, 0.89, 0);
        group.add(beltMesh);

        // Roletes de aço polido nas extremidades com flanges de centragem
        const rollerGeo = new THREE.CylinderGeometry(0.075, 0.075, 0.82, 24);
        const rollerL = new THREE.Mesh(rollerGeo, matSteel);
        rollerL.rotation.x = Math.PI / 2;
        rollerL.position.set(-3.34, 0.88, 0);
        const rollerR = new THREE.Mesh(rollerGeo, matSteel);
        rollerR.rotation.x = Math.PI / 2;
        rollerR.position.set(3.34, 0.88, 0);

        // Mancais de rolamento externos em ferro fundido
        const bearingGeo = new THREE.BoxGeometry(0.12, 0.14, 0.05);
        const bL1 = new THREE.Mesh(bearingGeo, matDark); bL1.position.set(-3.34, 0.88, 0.44);
        const bL2 = new THREE.Mesh(bearingGeo, matDark); bL2.position.set(-3.34, 0.88, -0.44);
        const bR1 = new THREE.Mesh(bearingGeo, matDark); bR1.position.set(3.34, 0.88, 0.44);
        const bR2 = new THREE.Mesh(bearingGeo, matDark); bR2.position.set(3.34, 0.88, -0.44);
        group.add(rollerL, rollerR, bL1, bL2, bR1, bR2);

        // 3. MOTORREDUTOR DE TRAÇÃO INDUSTRIAL (Acoplado ao rolo esquerdo)
        const motorGroup = new THREE.Group();
        motorGroup.position.set(-3.65, 0.88, 0);
        // Carcaça aletada do motor
        const motorBody = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.36, 20), matBlue);
        motorBody.rotation.z = Math.PI / 2;
        // Redutor de engrenagens
        const gearbox = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.30, 0.30), matDark);
        gearbox.position.set(0.15, 0, 0);
        // Caixa de bornes de ligação superior
        const terminalBox = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.09, 0.14), matDark);
        terminalBox.position.set(-0.06, 0.18, 0);
        // Acoplamento mecânico com o eixo do rolete
        const coupling = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.18, 16), matSteel);
        coupling.rotation.z = Math.PI / 2;
        coupling.position.set(0.24, 0, 0);
        // Tampa e hélice de ventilação visível giratória
        const fanCover = new THREE.Mesh(new THREE.CylinderGeometry(0.145, 0.145, 0.08, 20), matYellow);
        fanCover.rotation.z = Math.PI / 2;
        fanCover.position.set(-0.21, 0, 0);
        motorFanMesh = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.22, 0.22), matDark);
        motorFanMesh.position.set(-0.22, 0, 0);
        motorGroup.add(motorBody, gearbox, terminalBox, coupling, fanCover, motorFanMesh);
        group.add(motorGroup);

        // 4. SUPORTE EM PÓRTICO MODERNO DOS SENSORES (Não obstrui a vista da esteira)
        const archGroup = new THREE.Group();
        archGroup.position.set(POS_X_OPT, 0.90, 0);
        // Colunas e travessa do pórtico fino
        const archLeg1 = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.46, 0.04), matAlum);
        archLeg1.position.set(0, 0.23, -0.43);
        const archLeg2 = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.46, 0.04), matAlum);
        archLeg2.position.set(0, 0.23, 0.43);
        const archTop = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.04, 0.90), matAlum);
        archTop.position.set(0, 0.46, 0);
        archGroup.add(archLeg1, archLeg2, archTop);

        // Sensor Óptico de Barreira Retroreflexivo (S_OPT)
        const optSensorGroup = new THREE.Group();
        optSensorGroup.position.set(0, 0.40, 0);
        const optBody = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.14, 16), matDark);
        const optHead = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.034, 0.03, 16), matSteel);
        optHead.position.y = -0.06;
        // Lente óptica emissora vermelha
        const optLens = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.01, 16), new THREE.MeshBasicMaterial({ color: 0x991B1B }));
        optLens.position.y = -0.075;
        // LED de Status Bicolor no topo (Verde normal / Vermelho quando detecta)
        ledOptMesh = new THREE.Mesh(new THREE.SphereGeometry(0.016, 12, 12), new THREE.MeshBasicMaterial({ color: 0x22C55E }));
        ledOptMesh.position.set(0, 0.08, 0);
        // Feixe óptico transparente vermelho até o tapete
        laserBeamMesh = new THREE.Mesh(
            new THREE.CylinderGeometry(0.003, 0.012, 0.38, 8),
            new THREE.MeshBasicMaterial({ color: 0xEF4444, transparent: true, opacity: 0.18 })
        );
        laserBeamMesh.position.y = -0.22;
        optSensorGroup.add(optBody, optHead, optLens, ledOptMesh, laserBeamMesh);
        archGroup.add(optSensorGroup);
        group.add(archGroup);

        // 5. SENSOR DE PROXIMIDADE INDUTIVO M18 (S_IND)
        const indGroup = new THREE.Group();
        indGroup.position.set(POS_X_IND, 0.90, 0);
        // Braço de sustentação em cantoneira
        const indArm = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.03, 0.48), matAlum);
        indArm.position.set(0, 0.34, -0.21);
        const indArmStand = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.36, 0.04), matAlum);
        indArmStand.position.set(0, 0.18, -0.43);
        indGroup.add(indArm, indArmStand);

        // Corpo cilíndrico de latão niquelado M18 com rosca
        const indSensorBody = new THREE.Group();
        indSensorBody.position.set(0, 0.28, 0);
        const indBody = new THREE.Mesh(new THREE.CylinderGeometry(0.036, 0.036, 0.18, 16), matBrass);
        const indNut1 = new THREE.Mesh(new THREE.CylinderGeometry(0.044, 0.044, 0.02, 6), matSteel);
        indNut1.position.y = 0.04;
        const indNut2 = new THREE.Mesh(new THREE.CylinderGeometry(0.044, 0.044, 0.02, 6), matSteel);
        indNut2.position.y = -0.04;
        // Face sensora ativa em PBT laranja/turquesa de alta visibilidade
        const indFace = new THREE.Mesh(new THREE.CylinderGeometry(0.034, 0.034, 0.02, 16), matYellow);
        indFace.position.y = -0.095;
        // Anel LED indicador de detecção de metal (360°)
        ledIndMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.038, 0.038, 0.015, 16), new THREE.MeshBasicMaterial({ color: 0x334155 }));
        ledIndMesh.position.y = 0.08;
        indSensorBody.add(indBody, indNut1, indNut2, indFace, ledIndMesh);
        indGroup.add(indSensorBody);
        group.add(indGroup);

        // 6. CILINDRO PNEUMÁTICO EMPURRADOR FESTO (Montado na traseira, desliza em direção à frente)
        const pusherGroup = new THREE.Group();
        pusherGroup.position.set(POS_X_PUSHER, 0.95, -0.74);
        // Bloco do cilindro perfilado com ranhuras para sensores S0/S1
        const cylBody = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.38), matAlum);
        const cylFrontCap = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.13, 0.04), matDark);
        cylFrontCap.position.set(0, 0, 0.18);
        const cylRearCap = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.13, 0.04), matDark);
        cylRearCap.position.set(0, 0, -0.18);

        // Conexões pneumáticas com mangueira azul
        const fittingPneu1 = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.04, 12), matBrass);
        fittingPneu1.position.set(0, 0.075, 0.14);
        const fittingPneu2 = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.04, 12), matBrass);
        fittingPneu2.position.set(0, 0.075, -0.14);

        // Haste de inox cromada deslizante
        pusherPistonMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.48, 16), matSteel);
        pusherPistonMesh.rotation.x = Math.PI / 2;
        pusherPistonMesh.position.set(0, 0, 0.20);

        // Sapatilha empurradora frontal com batente em borracha preta
        pusherShoeMesh = new THREE.Group();
        pusherShoeMesh.position.set(0, 0, 0.44);
        const shoePlate = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.09, 0.025), matAlumDark);
        const shoeRubber = new THREE.Mesh(new THREE.BoxGeometry(0.27, 0.08, 0.015), matRubber);
        shoeRubber.position.set(0, 0, 0.018);
        pusherShoeMesh.add(shoePlate, shoeRubber);

        pusherGroup.add(cylBody, cylFrontCap, cylRearCap, fittingPneu1, fittingPneu2, pusherPistonMesh, pusherShoeMesh);
        group.add(pusherGroup);

        // 7. RAMPA LATERAL DE METAIS E CONTAINER INDUSTRIAL (KLT Eurobox)
        const rampGroup = new THREE.Group();
        rampGroup.position.set(POS_X_PUSHER, 0.68, 0.82);
        // Chapa inclinada em aço inox com abas de contenção
        const rampFloor = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.02, 0.64), matSteel);
        rampFloor.rotation.x = 0.36;
        rampFloor.position.set(0, 0.12, 0);
        const rampSideL = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.64), matSteel);
        rampSideL.rotation.x = 0.36;
        rampSideL.position.set(-0.22, 0.15, 0);
        const rampSideR = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.08, 0.64), matSteel);
        rampSideR.rotation.x = 0.36;
        rampSideR.position.set(0.22, 0.15, 0);
        rampGroup.add(rampFloor, rampSideL, rampSideR);

        // Container Industrial KLT de Metais (Caixa empilhável com nervuras de reforço)
        const metalBox = new THREE.Group();
        metalBox.position.set(POS_X_PUSHER, 0.25, 1.45);
        const mBoxBody = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.44, 0.52), matYellow);
        const mBoxRim = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.05, 0.56), matDark);
        mBoxRim.position.y = 0.20;
        // Placa serigráfica de identificação
        const mLabelCanvas = document.createElement('canvas');
        mLabelCanvas.width = 256; mLabelCanvas.height = 64;
        const mlCtx = mLabelCanvas.getContext('2d');
        mlCtx.fillStyle = '#0F172A'; mlCtx.fillRect(0, 0, 256, 64);
        mlCtx.fillStyle = '#FACC15'; mlCtx.font = 'bold 22px sans-serif';
        mlCtx.textAlign = 'center'; mlCtx.fillText('METAIS / ALUMÍNIO', 128, 40);
        const mLabelTex = new THREE.CanvasTexture(mLabelCanvas);
        const mLabelMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.48, 0.12), new THREE.MeshBasicMaterial({ map: mLabelTex }));
        mLabelMesh.position.set(0, 0.05, 0.265);
        metalBox.add(mBoxBody, mBoxRim, mLabelMesh);
        group.add(rampGroup, metalBox);

        // Container Industrial KLT de Plásticos (Fim da esteira)
        const plasticBox = new THREE.Group();
        plasticBox.position.set(POS_X_END + 0.55, 0.30, 0.0);
        const pBoxBody = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.50, 0.62), matBlue);
        const pBoxRim = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.05, 0.66), matDark);
        pBoxRim.position.y = 0.23;
        const pLabelCanvas = document.createElement('canvas');
        pLabelCanvas.width = 256; pLabelCanvas.height = 64;
        const plCtx = pLabelCanvas.getContext('2d');
        plCtx.fillStyle = '#0F172A'; plCtx.fillRect(0, 0, 256, 64);
        plCtx.fillStyle = '#60A5FA'; plCtx.font = 'bold 22px sans-serif';
        plCtx.textAlign = 'center'; plCtx.fillText('PLÁSTICOS / POLÍMERO', 128, 40);
        const pLabelTex = new THREE.CanvasTexture(pLabelCanvas);
        const pLabelMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.50, 0.12), new THREE.MeshBasicMaterial({ map: pLabelTex }));
        pLabelMesh.rotation.y = -Math.PI / 2;
        pLabelMesh.position.set(-0.315, 0.05, 0);
        plasticBox.add(pBoxBody, pBoxRim, pLabelMesh);
        group.add(plasticBox);

        // 8. PAINEL CONSOLE FRONTAL DE BORNES DE E/S (Posicionado na base Y=0.22, Z=0.85 para não obstruir o tapete)
        const ioPanelGroup = new THREE.Group();
        ioPanelGroup.position.set(0, 0.22, 0.85);
        ioPanelGroup.rotation.x = -0.32; // Leve inclinação ergonômica voltada para cima

        // Plinto de suporte da bancada de bornes
        const ioStand = new THREE.Mesh(new THREE.BoxGeometry(3.30, 0.26, 0.10), matDark);
        ioPanelGroup.add(ioStand);

        // 6 Bornes banana padrão 4mm isolados com serigrafia
        Object.values(IO_TERMINALS).forEach(term => {
            const jackMat = new THREE.MeshStandardMaterial({
                color: term.isInput ? 0xEF4444 : (term.id === 'S_IND' ? 0xF59E0B : 0x22C55E),
                metalness: 0.85, roughness: 0.25
            });
            const jack = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.042, 0.06, 16), jackMat);
            jack.rotation.x = Math.PI / 2;
            jack.position.set(term.relX, 0.03, 0.055);

            // Rótulo serigráfico Canvas nítido
            const lblCanvas = document.createElement('canvas');
            lblCanvas.width = 160; lblCanvas.height = 56;
            const lCtx = lblCanvas.getContext('2d');
            lCtx.fillStyle = '#0F172A'; lCtx.fillRect(0, 0, 160, 56);
            lCtx.fillStyle = term.isInput ? '#FCA5A5' : (term.id === 'S_IND' ? '#FDE68A' : '#86EFAC');
            lCtx.font = 'bold 20px monospace';
            lCtx.textAlign = 'center';
            lCtx.fillText(term.id, 80, 36);
            const lblTex = new THREE.CanvasTexture(lblCanvas);
            const lblMesh = new THREE.Mesh(new THREE.PlaneGeometry(0.38, 0.13), new THREE.MeshBasicMaterial({ map: lblTex }));
            lblMesh.position.set(term.relX, -0.06, 0.055);

            ioPanelGroup.add(jack, lblMesh);
        });
        group.add(ioPanelGroup);

        return group;
    }

    /**
     * Cria uma nova peça com características físicas e materiais reais:
     * - Metal: Tarugo de alumínio usinado CNC polido (brilhante, reflexivo, com chanfro e anel central)
     * - Plástico: Cilindro de polímero de engenharia Delrin / POM (fosco, azul elétrico de alta visibilidade)
     */
    /**
     * Cria a geometria 3D da peça e insere no processo
     */
    function createPieceMesh(isMetal) {
        if (!isActive || !cellGroup) return;

        const pieceMeshGroup = new THREE.Group();

        if (isMetal) {
            // Tarugo de alumínio usinado CNC de alta fidelidade
            const matMetal = new THREE.MeshStandardMaterial({
                color: 0xF1F5F9,
                metalness: 0.96,
                roughness: 0.12
            });
            const body = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.20, 24), matMetal);
            const topRing = new THREE.Mesh(new THREE.TorusGeometry(0.065, 0.012, 12, 24), new THREE.MeshStandardMaterial({ color: 0x94A3B8, metalness: 0.95, roughness: 0.2 }));
            topRing.rotation.x = Math.PI / 2;
            topRing.position.y = 0.101;
            pieceMeshGroup.add(body, topRing);
        } else {
            // Tarugo de polímero técnico azul Delrin / POM
            const matPlastic = new THREE.MeshStandardMaterial({
                color: 0x1D4ED8,
                metalness: 0.03,
                roughness: 0.42
            });
            const body = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.20, 24), matPlastic);
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.02, 16), new THREE.MeshStandardMaterial({ color: 0x3B82F6, roughness: 0.5 }));
            cap.position.y = 0.101;
            pieceMeshGroup.add(body, cap);
        }

        pieceMeshGroup.position.set(-3.15, 1.01, 0); // Início do tapete ampliado
        cellGroup.add(pieceMeshGroup);

        const pieceObj = {
            mesh: pieceMeshGroup,
            isMetal,
            x: -3.15,
            y: 1.01,
            z: 0.0,
            status: 'conveyor', // 'conveyor', 'pushed', 'dropped'
            vy: 0.0,
            vz: 0.0
        };
        pieces.push(pieceObj);
        stats.totalSpawned++;
        updateHudStats();
    }

    /**
     * Invocador com Interlock Anti-Colisão Industrial:
     * Garante espaçamento físico mínimo de 1.35m entre peças consecutivas
     */
    function spawnPiece(forceMetal = null) {
        if (!isActive || !cellGroup) return;
        const isMetal = (forceMetal !== null) ? forceMetal : (Math.random() < 0.5);

        // Verifica se a zona de alimentação inicial (x < -1.80) já está ocupada
        const isIntakeOccupied = pieces.some(p => p.status === 'conveyor' && p.x < -1.80);

        if (isIntakeOccupied) {
            // Enfileira na rampa de entrada sem sobrepor peças
            if (pieceQueue.length < 5) {
                pieceQueue.push(isMetal);
                if (window.LogicCraft?.ui?.showToast && forceMetal !== null) {
                    window.LogicCraft.ui.showToast('info', `Peça (${isMetal ? 'Metal' : 'Plástico'}) colocada na fila de alimentação automatizada.`);
                }
            }
            return;
        }

        createPieceMesh(isMetal);
    }

    /**
     * Loop de simulação física das peças e acoplamento eletromecânico
     */
    function updatePhysics(dt, circuit) {
        if (!isActive || !cellGroup) return;

        // 1. Atualização do Motorredutor e Lona da esteira
        if (isBeltRunning) {
            if (beltTex) beltTex.offset.x -= (beltSpeed * dt * 0.25);
            if (motorFanMesh) motorFanMesh.rotation.x += (beltSpeed * dt * 18.0);
        }
        window.LogicCraft?.audio?.setMotorRunning(isBeltRunning);

        // Despacha peças enfileiradas assim que a entrada da esteira estiver livre
        if (isBeltRunning && pieceQueue.length > 0) {
            const isIntakeClear = !pieces.some(p => p.status === 'conveyor' && p.x < -1.80);
            if (isIntakeClear) {
                const nextIsMetal = pieceQueue.shift();
                createPieceMesh(nextIsMetal);
            }
        }

        // 2. Movimento do cilindro pneumático empurrador
        const pushRate = 7.0; // Resposta rápida de cilindro de curso curto
        const pushDiff = pusherTarget - pusherExtension;
        if (Math.abs(pushDiff) > 0.01) {
            pusherExtension = THREE.MathUtils.lerp(pusherExtension, pusherTarget, dt * pushRate);
        } else {
            pusherExtension = pusherTarget;
        }

        // Desloca o êmbolo e a sapatilha transversalmente (eixo Z)
        if (pusherPistonMesh) pusherPistonMesh.position.z = 0.20 + (pusherExtension * 0.46);
        if (pusherShoeMesh) pusherShoeMesh.position.z = 0.44 + (pusherExtension * 0.46);

        // Atualiza sensores de fim de curso do cilindro empurrador
        const prevS0 = sensorS0;
        const prevS1 = sensorS1;
        sensorS0 = (pusherExtension <= 0.08);
        sensorS1 = (pusherExtension >= 0.92);
        IO_TERMINALS.S0_REC.state = sensorS0;
        IO_TERMINALS.S1_AV.state = sensorS1;

        if ((!prevS0 && sensorS0) || (!prevS1 && sensorS1)) {
            window.LogicCraft?.audio?.playSensorBeep();
        }

        // 3. Atualização das peças sobre a esteira
        let optTriggered = false;
        let indTriggered = false;

        for (let i = pieces.length - 1; i >= 0; i--) {
            const p = pieces[i];

            if (p.status === 'conveyor') {
                if (isBeltRunning) {
                    p.x += beltSpeed * dt;
                    p.mesh.position.x = p.x;
                }

                // Sensor Óptico de Barreira (detecta passagem de qualquer peça)
                if (Math.abs(p.x - POS_X_OPT) < 0.26) {
                    optTriggered = true;
                }

                // Sensor Indutivo: detecta apenas peça de metal diretamente sob o sensor
                if (p.isMetal && p.x >= POS_X_IND - 0.25 && p.x <= POS_X_PUSHER + 0.20) {
                    indTriggered = true;
                }

                // Zona de ação do cilindro empurrador
                if (Math.abs(p.x - POS_X_PUSHER) < 0.25) {
                    if (pusherExtension > 0.20) {
                        // O pistão atingiu a peça: desvia para a rampa lateral!
                        p.status = 'pushed';
                        p.vz = 2.4;
                        p.vy = -0.5;
                        window.LogicCraft?.audio?.playPneumaticPuff(0.18, 0.30);
                    }
                }

                // Queda no fim da esteira (caixa de plásticos)
                if (p.x >= POS_X_END) {
                    p.status = 'dropped_end';
                    p.vy = -3.2;
                }
            } else if (p.status === 'pushed') {
                // Deslizando na rampa de metais
                p.z += p.vz * dt;
                p.y += p.vy * dt;
                p.mesh.position.set(p.x, p.y, p.z);

                if (p.z >= 1.40) {
                    // Caiu dentro do container de metais
                    if (p.isMetal) {
                        stats.metalsCorrect++;
                        window.LogicCraft?.audio?.playMetalClink();
                    } else {
                        stats.sortingErrors++;
                        window.LogicCraft?.audio?.playPlasticThud();
                        notifyWarning('Peça plástica empurrada por engano para a caixa de metais!');
                    }
                    cellGroup.remove(p.mesh);
                    pieces.splice(i, 1);
                    updateHudStats();
                    continue;
                }
            } else if (p.status === 'dropped_end') {
                // Caindo no container de plásticos no fim da linha
                p.y += p.vy * dt;
                p.mesh.position.y = p.y;

                if (p.y <= 0.48) {
                    if (!p.isMetal) {
                        stats.plasticsCorrect++;
                        window.LogicCraft?.audio?.playPlasticThud();
                    } else {
                        stats.sortingErrors++;
                        window.LogicCraft?.audio?.playMetalClink();
                        notifyWarning('Peça metálica não foi empurrada e caiu nos plásticos!');
                    }
                    cellGroup.remove(p.mesh);
                    pieces.splice(i, 1);
                    updateHudStats();
                    continue;
                }
            }
        }

        // Atualiza LEDs visuais e saídas elétricas dos sensores
        sensorOptActive = optTriggered;
        sensorIndActive = indTriggered;
        IO_TERMINALS.S_OPT.state = sensorOptActive;
        IO_TERMINALS.S_IND.state = sensorIndActive;

        if (ledOptMesh) ledOptMesh.material.color.setHex(sensorOptActive ? 0xEF4444 : 0x22C55E);
        if (ledIndMesh) ledIndMesh.material.color.setHex(sensorIndActive ? 0xF59E0B : 0x334155);
        if (laserBeamMesh) laserBeamMesh.material.opacity = sensorOptActive ? 0.85 : 0.18;

        // Acopla o sinal do sensor indutivo com os componentes da bancada (K2, Y1, H2)
        if (circuit) {
            const k2 = circuit.get(window.LogicCraft?.core?.key(-1.5, 0.0, -0.5));
            const y1 = circuit.get(window.LogicCraft?.core?.key(0.5, 0.0, -0.5));
            const h2 = circuit.get(window.LogicCraft?.core?.key(2.0, 0.0, -0.5));
            if (k2) { k2.state = indTriggered; }
            if (y1) { y1.state = indTriggered; }
            if (h2) { h2.state = indTriggered; }
        }

        // 4. Temporizador de geração automática de peças (respeita a desobstrução da entrada)
        if (isBeltRunning && pieceQueue.length === 0) {
            spawnTimer += dt;
            if (spawnTimer >= SPAWN_INTERVAL) {
                spawnTimer = 0.0;
                spawnPiece();
            }
        }
    }

    /**
     * Sincroniza a esteira e o pistão empurrador com o circuito elétrico da bancada
     */
    function syncWithSimulatorWires(circuit, airWires) {
        if (!isActive || !circuit) return;

        const core = window.LogicCraft?.core;

        // 1. Verifica estado de Emergência NR-12
        let emergencyLocked = false;
        for (const n of circuit.values()) {
            if (n.type === core.COMP.EMERGENCY_STOP && n.isEmergencyLocked) {
                emergencyLocked = true;
                break;
            }
        }

        // 2. Verifica se a esteira está energizada pelo relé K1 com selo
        let cmdBelt = false;
        if (!emergencyLocked) {
            const k1 = circuit.get(core.key(0.5, 0.0, 1.0));
            if (k1 && k1.state) {
                cmdBelt = true;
            } else {
                for (const n of circuit.values()) {
                    if ((n.type === core.COMP.RELAY || n.type === core.COMP.CONTACTOR_3P) && Math.abs(n.z - 1.0) < 0.25 && n.state) {
                        cmdBelt = true;
                        break;
                    }
                }
            }
        }
        isBeltRunning = cmdBelt;
        IO_TERMINALS.CMD_ESTEIRA.state = isBeltRunning;

        // 3. Verifica se a solenoide Y1 ou sensor indutivo estão ativos para disparar o empurrador
        const y1 = circuit.get(core.key(0.5, 0.0, -0.5));
        const k2 = circuit.get(core.key(-1.5, 0.0, -0.5));
        const cmdPusher = Boolean((y1 && y1.state) || (k2 && k2.state) || sensorIndActive);
        IO_TERMINALS.CMD_EMPURRADOR.state = cmdPusher;
        pusherTarget = cmdPusher ? 1.0 : 0.0;
    }

    /**
     * Interface gráfica flutuante (HUD) com placar e botões de teste
     */
    function createHudOverlay() {
        let hud = document.getElementById('conveyor-cell-hud');
        if (hud) hud.remove();

        hud = document.createElement('aside');
        hud.id = 'conveyor-cell-hud';
        hud.setAttribute('aria-label', 'Painel da Célula Didática');
        hud.style.cssText = `
            position: fixed;
            top: 60px;
            right: 20px;
            width: 320px;
            background: rgba(11, 32, 72, 0.92);
            border: 1px solid #38BDF8;
            border-radius: 12px;
            padding: 14px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.5);
            backdrop-filter: blur(12px);
            z-index: 55;
            color: #FFFFFF;
            font-family: inherit;
        `;

        hud.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #1E293B; padding-bottom: 8px; margin-bottom: 10px;">
                <h3 style="margin: 0; font-size: 0.95rem; font-weight: 800; color: #38BDF8; display: flex; align-items: center; gap: 6px;">
                    <span>🏭</span> Célula Seletora 4.0
                </h3>
                <span id="conveyor-status-pill" style="font-size: 0.70rem; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: rgba(239, 68, 68, 0.2); color: #FCA5A5; border: 1px solid #EF4444;">PARADA</span>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 0.75rem; margin-bottom: 12px;">
                <div style="background: rgba(7, 21, 47, 0.6); padding: 6px 8px; border-radius: 6px; border: 1px solid #1E293B;">
                    <div style="color: #94A3B8;">Metais Desviados</div>
                    <div id="stat-metals" style="font-size: 1.1rem; font-weight: 800; color: #FACC15;">0</div>
                </div>
                <div style="background: rgba(7, 21, 47, 0.6); padding: 6px 8px; border-radius: 6px; border: 1px solid #1E293B;">
                    <div style="color: #94A3B8;">Plásticos Aprovados</div>
                    <div id="stat-plastics" style="font-size: 1.1rem; font-weight: 800; color: #60A5FA;">0</div>
                </div>
                <div style="background: rgba(7, 21, 47, 0.6); padding: 6px 8px; border-radius: 6px; border: 1px solid #1E293B;">
                    <div style="color: #94A3B8;">Total Processado</div>
                    <div id="stat-total" style="font-size: 1.1rem; font-weight: 800; color: #FFFFFF;">0</div>
                </div>
                <div style="background: rgba(7, 21, 47, 0.6); padding: 6px 8px; border-radius: 6px; border: 1px solid #1E293B;">
                    <div style="color: #94A3B8;">Falhas de Triagem</div>
                    <div id="stat-errors" style="font-size: 1.1rem; font-weight: 800; color: #EF4444;">0</div>
                </div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 6px;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">
                    <button id="btn-spawn-metal" class="btn-act" style="justify-content: center; font-size: 0.72rem; padding: 6px 8px;">+ Peça Metal</button>
                    <button id="btn-spawn-plastic" class="btn-act" style="justify-content: center; font-size: 0.72rem; padding: 6px 8px;">+ Peça Plástico</button>
                </div>
                <button id="btn-reset-stats" class="btn-act" style="justify-content: center; font-size: 0.72rem; padding: 6px 8px;">🔄 Zerar Placar</button>
                <button id="btn-close-cell" class="btn-act btn-danger" style="justify-content: center; font-size: 0.72rem; padding: 6px 8px; margin-top: 4px;">❌ Fechar Célula Didática</button>
            </div>
        `;

        document.body.appendChild(hud);

        document.getElementById('btn-spawn-metal')?.addEventListener('click', () => spawnPiece(true));
        document.getElementById('btn-spawn-plastic')?.addEventListener('click', () => spawnPiece(false));
        document.getElementById('btn-reset-stats')?.addEventListener('click', () => {
            stats = { totalSpawned: 0, metalsCorrect: 0, plasticsCorrect: 0, sortingErrors: 0 };
            updateHudStats();
        });
        document.getElementById('btn-close-cell')?.addEventListener('click', () => closeDemo());
    }

    function updateHudStats() {
        const elMetals = document.getElementById('stat-metals');
        const elPlastics = document.getElementById('stat-plastics');
        const elTotal = document.getElementById('stat-total');
        const elErrors = document.getElementById('stat-errors');
        const elPill = document.getElementById('conveyor-status-pill');

        if (elMetals) elMetals.textContent = stats.metalsCorrect;
        if (elPlastics) elPlastics.textContent = stats.plasticsCorrect;
        if (elTotal) elTotal.textContent = stats.totalSpawned;
        if (elErrors) elErrors.textContent = stats.sortingErrors;

        if (elPill) {
            let isEmergency = false;
            const core = window.LogicCraft?.core;
            const state = window.LogicCraft?.state;
            if (state?.circuit) {
                for (const n of state.circuit.values()) {
                    if (n.type === core?.COMP?.EMERGENCY_STOP && n.isEmergencyLocked) {
                        isEmergency = true;
                        break;
                    }
                }
            }

            if (isEmergency) {
                elPill.textContent = 'EMERGÊNCIA (NR-12)';
                elPill.style.background = 'rgba(239, 68, 68, 0.35)';
                elPill.style.color = '#FCA5A5';
                elPill.style.borderColor = '#EF4444';
            } else if (isBeltRunning) {
                elPill.textContent = 'EM OPERAÇÃO';
                elPill.style.background = 'rgba(34, 197, 94, 0.2)';
                elPill.style.color = '#86EFAC';
                elPill.style.borderColor = '#22C55E';
            } else {
                elPill.textContent = 'PARADA';
                elPill.style.background = 'rgba(239, 68, 68, 0.2)';
                elPill.style.color = '#FCA5A5';
                elPill.style.borderColor = '#EF4444';
            }
        }
    }

    function notifyWarning(msg) {
        if (window.LogicCraft?.ui?.showToast) {
            window.LogicCraft.ui.showToast('warning', `⚠️ ${msg}`);
        }
    }

    /**
     * Carrega a Demonstração Completa da Célula Didática com Circuito Teórico Montado
     */
    function openDemo() {
        const sim = window.LogicCraft?.simulation;
        const core = window.LogicCraft?.core;
        if (!sim || !core) return;

        window.isBatchLoading = true;
        try {
            // 1. Limpa a bancada anterior
            sim.clearCircuit(true);

            // 2. Cria a geometria 3D da esteira na cena
            if (cellGroup) {
                window.LogicCraft.scene?.remove(cellGroup);
            }
            cellGroup = buildCell3DModel();
            window.LogicCraft.scene?.add(cellGroup);

            isActive = true;
            stats = { totalSpawned: 0, metalsCorrect: 0, plasticsCorrect: 0, sortingErrors: 0 };
            createHudOverlay();

            // 3. Monta o Painel de Comando e Segurança Industrial na Bancada (NR-12)
            // Botão de Emergência com Trava Cogumelo (S0)
            sim.addBlock(-4.5, 0.0, 1.0, { type: core.COMP.EMERGENCY_STOP, rot: 0 }, true);
            // Botoeira Vermelha NF de Parada (S1)
            sim.addBlock(-3.0, 0.0, 1.0, { type: core.COMP.PUSH_BUTTON_NC, rot: 0 }, true);
            // Botoeira Verde NA de Partida (S2)
            sim.addBlock(-1.5, 0.0, 1.0, { type: core.COMP.PUSH_BUTTON_NO, rot: 0 }, true);
            // Relé de Potência K1 com Selo (Comando do Motor) - Inicia ativo com selo pré-energizado
            sim.addBlock(0.5, 0.0, 1.0, { type: core.COMP.RELAY, state: true, manualValue: true, rot: 0 }, true);
            // Lâmpada Piloto Verde H1 ("Esteira em Operação")
            sim.addBlock(2.0, 0.0, 1.0, { type: core.COMP.LAMP, rot: 0 }, true);

            // 4. Monta o Circuito Eletropneumático de Triagem Automática
            // Relé Auxiliar K2 (Comando de Detecção do Sensor Indutivo)
            sim.addBlock(-1.5, 0.0, -0.5, { type: core.COMP.RELAY, rot: 0 }, true);
            // Válvula Solenoide Pneumática Y1 (Empurrador de Metais)
            sim.addBlock(0.5, 0.0, -0.5, { type: core.COMP.SOLENOID_PNEU, rot: 0 }, true);
            // Lâmpada Piloto Amarela H2 ("Metal Detectado")
            sim.addBlock(2.0, 0.0, -0.5, { type: core.COMP.LAMP, rot: 0 }, true);

            // 5. Conexões de Fiação Industrial (Air Wires com Jumpers)
            // Botoeira NF de Parada alimenta o Comum do Relé K1
            sim.addAirWire({ nodeX: -3.0, nodeY: 0.0, nodeZ: 1.0, pinId: 'OUT' }, { nodeX: 0.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'COM' }, true);
            // Botoeira NA de Partida energiza a Bobina A1 de K1
            sim.addAirWire({ nodeX: -1.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'OUT' }, { nodeX: 0.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'A1' }, true);
            // Contato Auxiliar NO de K1 em paralelo com Partida (Circuito de Selo / Autorretenção)
            sim.addAirWire({ nodeX: 0.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'NO' }, { nodeX: 0.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'A1' }, true);
            // Saída NO de K1 alimenta a Lâmpada Verde H1
            sim.addAirWire({ nodeX: 0.5, nodeY: 0.0, nodeZ: 1.0, pinId: 'NO' }, { nodeX: 2.0, nodeY: 0.0, nodeZ: 1.0, pinId: 'X1' }, true);

            // Circuito de Triagem:
            // Alimentação 24V de Parada alimenta o Comum do Relé K2
            sim.addAirWire({ nodeX: -3.0, nodeY: 0.0, nodeZ: 1.0, pinId: 'OUT' }, { nodeX: -1.5, nodeY: 0.0, nodeZ: -0.5, pinId: 'COM' }, true);
            // Contato NO de K2 pilota a Bobina da Solenoide Y1
            sim.addAirWire({ nodeX: -1.5, nodeY: 0.0, nodeZ: -0.5, pinId: 'NO' }, { nodeX: 0.5, nodeY: 0.0, nodeZ: -0.5, pinId: 'A1' }, true);
            // Contato NO de K2 acende a Lâmpada Amarela H2 (Detecção Ativa)
            sim.addAirWire({ nodeX: -1.5, nodeY: 0.0, nodeZ: -0.5, pinId: 'NO' }, { nodeX: 2.0, nodeY: 0.0, nodeZ: -0.5, pinId: 'X1' }, true);

            // 6. Posiciona a câmera em ângulo isométrico didático amplo
            if (sim.camera) {
                sim.camera.position.set(0.0, 4.8, 5.6);
                sim.camera.lookAt(0.0, 0.8, -0.8);
            }
        } finally {
            window.isBatchLoading = false;
        }

        sim.solveCircuit();
        isBeltRunning = true;

        if (window.LogicCraft?.ui?.showToast) {
            window.LogicCraft.ui.showToast('success', '🏭 Célula Esteira Seletora Carregada! Circuito teórico de comando e triagem ativo.');
        }
    }

    /**
     * Fecha a Célula Didática e restaura a bancada normal
     */
    function closeDemo() {
        isActive = false;
        isBeltRunning = false;
        window.LogicCraft?.audio?.setMotorRunning(false);

        if (cellGroup && window.LogicCraft.scene) {
            window.LogicCraft.scene.remove(cellGroup);
            cellGroup = null;
        }

        // Remove peças restantes
        pieces.forEach(p => {
            if (p.mesh && p.mesh.parent) p.mesh.parent.remove(p.mesh);
        });
        pieces = [];
        pieceQueue = [];

        const hud = document.getElementById('conveyor-cell-hud');
        if (hud) hud.remove();

        const sim = window.LogicCraft?.simulation;
        if (sim && sim.resetCameraView) {
            sim.resetCameraView();
        }
    }

    /**
     * Chamada contínua no loop de animação (animate)
     */
    function update(dt, circuit, airWires) {
        if (!isActive) return;
        syncWithSimulatorWires(circuit, airWires);
        updatePhysics(dt, circuit);
    }

    return {
        openDemo,
        closeDemo,
        update,
        spawnPiece,
        isActive: () => isActive
    };
})();

